import { defineStore } from 'pinia';
import { computed, ref, watch } from '@vue/runtime-core';
import { useCurrentUser, useCurrentBrand, useAuthToken } from '@/utils/storage';

/**
 * Store principal de l'application
 * Gère l'état global de manière réactive avec synchronisation localStorage
 */
export const useAppStore = defineStore('app', () => {
  // Utiliser les hooks réactifs du storage
  const { userData, isLoggedIn, userId, userName, userEmail, logout } = useCurrentUser();
  const { brand, setBrand, brandDisplayName, brandLogo } = useCurrentBrand();
  const { token, isAuthenticated, setToken, clearToken } = useAuthToken();

  // États locaux de l'application
  const loading = ref(false);
  const error = ref<string | null>(null);
  const notifications = ref<Array<{ id: string; message: string; type: 'success' | 'error' | 'info' }>>([]);

  // État de la sidebar/navigation
  const sidebarOpen = ref(true);
  const mobileMenuOpen = ref(false);

  // Computed properties
  const currentBrand = computed(() => brand.value);
  const currentUser = computed(() => userData.value);

  /**
   * Change la marque courante et recharge les données si nécessaire
   */
  const changeBrand = async (newBrand: string) => {
    const oldBrand = brand.value;

    if (oldBrand === newBrand.toLowerCase()) {
      return; // Pas de changement
    }

    try {
      loading.value = true;
      setBrand(newBrand);

      // Invalider les caches liés à la marque
      const { clearCache } = await import('@/utils/cache');
      await clearCache();

      // Émettre un événement pour notifier les stores
      window.dispatchEvent(new CustomEvent('brand-changed', {
        detail: { oldBrand, newBrand: newBrand.toLowerCase() }
      }));
    } catch (err) {
      console.error('Erreur lors du changement de marque:', err);
      error.value = 'Erreur lors du changement de marque';
      // Rollback
      setBrand(oldBrand);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Rafraîchit les données utilisateur depuis l'API
   */
  const refreshUserData = async () => {
    if (!token.value) {
      console.warn('⚠️  Pas de token, impossible de rafraîchir les données utilisateur');
      return;
    }

    try {
      const api = await import('@/api/api');
      const response = await api.default.get('/me');

      if (response.data) {
        // Utiliser setUserData pour mettre à jour de manière réactive
        const { setUserData } = useCurrentUser();
        setUserData(response.data);

        if (import.meta.env.DEV) {
          console.log('✅ Données utilisateur rafraîchies depuis l\'API');
        }
      }
    } catch (err) {
      console.error('❌ Erreur lors du rafraîchissement des données utilisateur:', err);
      // Ne pas afficher d'erreur à l'utilisateur pour ne pas le perturber
    }
  };

  /**
   * Connexion utilisateur
   */
  const login = async (user: any, authToken: string) => {
    try {
      loading.value = true;
      userData.value = user;
      setToken(authToken);

      addNotification('Connexion réussie', 'success');
    } catch (err) {
      console.error('Erreur lors de la connexion:', err);
      error.value = 'Erreur lors de la connexion';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Déconnexion utilisateur
   */
  const logoutUser = () => {
    logout();
    clearToken();

    // Nettoyer les caches
    import('@/utils/cache').then(({ clearCache }) => {
      clearCache();
    });

    addNotification('Déconnexion réussie', 'info');
  };

  /**
   * Ajoute une notification
   */
  const addNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = `notif-${Date.now()}-${Math.random()}`;
    notifications.value.push({ id, message, type });

    // Auto-suppression après 5 secondes
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  /**
   * Supprime une notification
   */
  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id);
    if (index !== -1) {
      notifications.value.splice(index, 1);
    }
  };

  /**
   * Toggle sidebar
   */
  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value;
  };

  /**
   * Toggle mobile menu
   */
  const toggleMobileMenu = () => {
    mobileMenuOpen.value = !mobileMenuOpen.value;
  };

  /**
   * Définir l'état de chargement global
   */
  const setLoading = (isLoading: boolean) => {
    loading.value = isLoading;
  };

  /**
   * Définir une erreur globale
   */
  const setError = (errorMessage: string | null) => {
    error.value = errorMessage;
    if (errorMessage) {
      addNotification(errorMessage, 'error');
    }
  };

  /**
   * Nettoyer l'erreur
   */
  const clearError = () => {
    error.value = null;
  };

  // Watcher pour logger les changements de marque (debug)
  if (import.meta.env.DEV) {
    watch(brand, (newBrand, oldBrand) => {
      console.log('🏷️  Marque changée:', { oldBrand, newBrand });
    });

    watch(userData, (newUser) => {
      console.log('👤 Utilisateur:', newUser ? `${newUser.name} (${newUser.email})` : 'Non connecté');
    });
  }

  return {
    // État
    loading,
    error,
    notifications,
    sidebarOpen,
    mobileMenuOpen,

    // User
    userData,
    isLoggedIn,
    userId,
    userName,
    userEmail,
    currentUser,

    // Brand
    brand,
    currentBrand,
    brandDisplayName,
    brandLogo,

    // Auth
    token,
    isAuthenticated,

    // Actions
    changeBrand,
    refreshUserData,
    login,
    logoutUser,
    setLoading,
    setError,
    clearError,
    addNotification,
    removeNotification,
    toggleSidebar,
    toggleMobileMenu,
  };
});
